/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Shape translation between the new server and the legacy admin store.
 *
 * The server is the canonical surface: it returns bare arrays of entities keyed
 * by their natural id (`siteId`, `uuid`, per-site `sequenceId`). The redux code
 * in this app predates that and reads `response.data.items` with Mongo `_id`
 * keys and flat `queueIds` / `boothIds` reference arrays.
 *
 * Rather than rewrite ~20 reducers, selectors and decorators, the translation
 * lives here at the API boundary. `cloudId` is the original Mongo `_id` that the
 * migration carried over for exactly this reason, so it is what `_id` resolves
 * to; the natural id stays on `id`/`uuid` and `siteId` rides along so writes can
 * be routed back to a site-scoped endpoint.
 */

export interface LegacyEnvelope<T = any> {
  ok: true;
  httpStatus: 200;
  data: T;
}

export function list(items: any[]): LegacyEnvelope<{ items: any[] }> {
  return { ok: true, httpStatus: 200, data: { items } };
}

export function object<T>(data: T): LegacyEnvelope<T> {
  return { ok: true, httpStatus: 200, data };
}

/**
 * Rejection shaped like `handleHttpErrors`, so an operation the server no longer
 * offers fails the same way a 404 would: the `run()` saga helper routes it to
 * HTTP_ERROR_SAGA and formik surfaces it, instead of a raw TypeError.
 */
export function unsupported(operation: string): Promise<never> {
  return Promise.reject({
    error: true,
    ok: false,
    httpStatus: 501,
    operation,
    responseBody: `${operation} has no endpoint on this server`
  });
}

/** Where a write for a given entity has to be sent. */
export interface Route {
  siteId: string;
  /** The id the site-scoped route expects — sequenceId for queues, uuid for devices. */
  nativeId: string;
}

const routes = new Map<string, Route>();

export function routeFor(id: any): Route | undefined {
  return routes.get(String(id));
}

/** Index an entity under every id the UI might hand back to us. */
function remember(route: Route, ...ids: any[]) {
  for (const id of ids) {
    if (id !== undefined && id !== null && id !== '') {
      routes.set(String(id), route);
    }
  }
}

export function forgetRoutes() {
  routes.clear();
}

const refIds = (refs: any[] | undefined) =>
  (refs ?? []).map((r: any) => r?.cloudId ?? r?.id).filter(Boolean);

export function site(s: any) {
  // siteId *is* the old ControlBox uuid, so `uuid` stays meaningful.
  const out = { ...s, _id: s.cloudId ?? s.siteId, id: s.siteId, uuid: s.siteId };
  remember({ siteId: s.siteId, nativeId: s.siteId }, out._id, s.siteId);
  return out;
}

export function queue(q: any, s: any) {
  const out = {
    ...q,
    _id: q.cloudId ?? q.id,
    id: q.id,
    sequenceId: q.id,
    siteId: s.siteId,
    controlBoxId: s._id
  };
  remember({ siteId: s.siteId, nativeId: q.id }, out._id, q.id);
  return out;
}

export function booth(b: any, s: any) {
  // `queues` is dropped, not kept alongside `queueIds`: the store's decorators
  // resolve references into full entities under those same names and spread the
  // raw entity last, so leaving the server's `[{cloudId, id}]` refs in place
  // silently overwrites the resolved objects.
  const { queues, ...rest } = b;
  const out = {
    ...rest,
    _id: b.cloudId ?? b.uuid,
    id: b.uuid,
    queueIds: refIds(queues),
    siteId: s.siteId,
    controlBoxId: s._id
  };
  remember({ siteId: s.siteId, nativeId: b.uuid }, out._id, b.uuid);
  return out;
}

export function display(d: any, s: any) {
  // See `booth` — the raw refs must not survive alongside the flattened ids.
  const { queues, booths, ...rest } = d;
  const out = {
    ...rest,
    _id: d.cloudId ?? d.uuid,
    id: d.uuid,
    queueIds: refIds(queues),
    boothIds: refIds(booths),
    siteId: s.siteId,
    controlBoxId: s._id
  };
  remember({ siteId: s.siteId, nativeId: d.uuid }, out._id, d.uuid);
  return out;
}

export function kiosk(k: any, s: any) {
  const out = {
    ...k,
    _id: k.cloudId ?? k.uuid,
    id: k.uuid,
    siteId: s.siteId,
    controlBoxId: s._id
  };
  remember({ siteId: s.siteId, nativeId: k.uuid }, out._id, k.uuid);
  return out;
}

/** Services, locations and users keep their Mongo id as the server's `id`. */
export function flat(e: any) {
  return { ...e, _id: e.id };
}
